import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/Loaders";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { House, Users, ClipboardText, CheckCircle, Flag, CurrencyInr, Bank, Wallet, ChatCircleText, Buildings, Warning, ChartBar } from "@phosphor-icons/react";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-stone-200 bg-white p-4">
    <div className="flex items-center gap-2 text-stone-400"><Icon size={16} weight="duotone" className="text-primary" /><span className="text-xs font-medium">{label}</span></div>
    <p className="mt-1.5 font-heading text-2xl font-bold text-stone-900">{value}</p>
  </div>
);

const Milestone = ({ label, m, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-stone-200 bg-white p-4">
    <div className="flex items-center justify-between"><span className="text-sm font-medium text-stone-700">{label}</span><span className="font-heading font-bold text-primary">{m.current}/{m.target}</span></div>
    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-stone-100"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${m.pct}%` }} /></div>
    <p className="mt-1 text-[11px] text-stone-400">{m.pct}% of target</p>
  </div>
);

export default function AdminLaunchDashboard() {
  const { user } = useAuth();
  const isSuper = (user?.admin_role || "SUPER") === "SUPER";
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => api.get("/admin/launch-dashboard").then((r) => setD(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const toggle = async (key, val) => {
    try { await api.post("/admin/emergency-controls", { [key]: val }); toast.success(`${key.replace("_", " ")} ${val ? "enabled" : "disabled"}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update."); }
  };

  if (err) return <DashboardLayout nav={adminNav} title="Admin"><div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700" data-testid="dash-error">Couldn't load the launch dashboard. Please refresh.</div></DashboardLayout>;
  if (!d) return <DashboardLayout nav={adminNav} title="Admin"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;
  const k = d.kpis;

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex items-center gap-2"><ChartBar size={26} className="text-primary" /><h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Launch Dashboard</h1></div>
      <p className="mt-1 text-sm text-stone-500">Live business metrics — real records only. Current status: <span className="font-semibold text-stone-800">{(d.launch_mode || "").replace("_", " ")}</span>.</p>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat icon={House} label="Live Properties" value={k.live_properties} testid="kpi-live-properties" />
        <Stat icon={Users} label="Customers" value={k.customers} testid="kpi-customers" />
        <Stat icon={ClipboardText} label="Booking Requests" value={k.booking_requests} testid="kpi-requests" />
        <Stat icon={CheckCircle} label="Confirmed" value={k.confirmed_bookings} testid="kpi-confirmed" />
        <Stat icon={Flag} label="Completed" value={k.completed_bookings} testid="kpi-completed" />
        <Stat icon={CurrencyInr} label="Booking Value" value={formatINR(k.booking_value)} testid="kpi-booking-value" />
        <Stat icon={Bank} label="Avinya Commission" value={formatINR(k.commission)} testid="kpi-commission" />
        <Stat icon={Wallet} label="Owner Payouts Paid" value={formatINR(k.owner_payouts_paid)} testid="kpi-payouts" />
        <Stat icon={ChatCircleText} label="Customer Leads" value={k.customer_leads} testid="kpi-customer-leads" />
        <Stat icon={Buildings} label="Owner Leads" value={k.owner_leads} testid="kpi-owner-leads" />
        <Stat icon={CurrencyInr} label="Advance Collected" value={formatINR(k.advance_collected)} testid="kpi-advance" />
        <Stat icon={Warning} label="Cancelled" value={k.cancelled_bookings} testid="kpi-cancelled" />
      </div>

      <h2 className="mt-8 font-heading text-lg font-semibold text-stone-900">Property Milestones</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <Milestone label="First 10 Live Properties" m={d.property_milestones.first_10} testid="ms-prop-10" />
        <Milestone label="First 50 Live Properties" m={d.property_milestones.first_50} testid="ms-prop-50" />
        <Milestone label="First 100 Live Properties" m={d.property_milestones.first_100} testid="ms-prop-100" />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        {Object.entries(d.properties_by_category).map(([c, n]) => (
          <div key={c} className="rounded-xl border border-stone-100 bg-white p-3 text-center"><p className="font-heading text-xl font-bold text-stone-900">{n}</p><p className="text-xs text-stone-400">{c}</p></div>
        ))}
      </div>

      <h2 className="mt-8 font-heading text-lg font-semibold text-stone-900">Booking Milestones</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <Milestone label="First 10 Bookings" m={d.booking_milestones.first_10} testid="ms-book-10" />
        <Milestone label="First 50 Bookings" m={d.booking_milestones.first_50} testid="ms-book-50" />
        <Milestone label="First 100 Bookings" m={d.booking_milestones.first_100} testid="ms-book-100" />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        {[["Confirmed", d.booking_milestones.breakdown.confirmed, "text-green-600"], ["Completed", d.booking_milestones.breakdown.completed, "text-blue-600"], ["Cancelled", d.booking_milestones.breakdown.cancelled, "text-red-500"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-stone-100 bg-white p-3 text-center"><p className={`font-heading text-xl font-bold ${c}`}>{v}</p><p className="text-xs text-stone-400">{l}</p></div>
        ))}
      </div>

      <h2 className="mt-8 font-heading text-lg font-semibold text-stone-900">Customer Target</h2>
      <div className="mt-3 md:max-w-md"><Milestone label="Toward 1,000 Customers" m={d.customer_target} testid="ms-customers" /></div>

      <h2 className="mt-10 flex items-center gap-2 font-heading text-lg font-semibold text-red-700"><Warning size={20} weight="fill" className="text-red-500" /> Emergency Controls</h2>
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between rounded-2xl border-2 border-dashed border-red-200 bg-red-50/40 p-4">
          <div><p className="font-medium text-stone-800">Pause new bookings</p><p className="text-xs text-stone-500">Existing bookings stay accessible; customers see a "temporarily unavailable" message.</p></div>
          <Switch data-testid="toggle-bookings-paused" disabled={!isSuper} checked={d.emergency.bookings_paused} onCheckedChange={(v) => toggle("bookings_paused", v)} />
        </div>
        <div className="flex items-center justify-between rounded-2xl border-2 border-dashed border-red-200 bg-red-50/40 p-4">
          <div><p className="font-medium text-stone-800">Pause new payments</p><p className="text-xs text-stone-500">Blocks new payment orders without touching existing payment records.</p></div>
          <Switch data-testid="toggle-payments-paused" disabled={!isSuper} checked={d.emergency.payments_paused} onCheckedChange={(v) => toggle("payments_paused", v)} />
        </div>
        {!isSuper && <p className="text-xs text-stone-400">Only a Super Admin can toggle emergency controls.</p>}
      </div>
    </DashboardLayout>
  );
}
